import { jsxs, jsx } from "react/jsx-runtime";
import React__default, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { AnalyticsId } from "./constants.js";
import { usePseudoClasses } from "./hooks/usePseudoClasses.js";
import { useSize } from "./hooks/useSize.js";
import { useUuid } from "./hooks/useUuid.js";
import { getAriaLabelAttributes } from "./utils/accessibility.js";
import { mergeRefs, isMutableRefObject } from "./utils/refs.js";
import { E as ErrorWrapper } from "./ErrorWrapper.js";
import { T as Title } from "./Title.js";
import styles from "./components/Slider/styles.module.scss";
const useSafeNumberValue = (initial, min, max) => {
  const [value, setValue] = useState(initial);
  const setSafeValue = (newValue) => {
    if (newValue > max) {
      setValue(max);
    } else if (newValue < min) {
      setValue(min);
    } else {
      setValue(newValue);
    }
  };
  useEffect(() => {
    setSafeValue(initial);
  }, [min, max]);
  return [value, setSafeValue];
};
const Slider = React__default.forwardRef((props, ref) => {
  const {
    title,
    ariaLabel,
    error,
    errorText,
    errorTextId,
    errorWrapperClassName,
    labelLeft,
    labelRight,
    disabled = false,
    onChange,
    steps,
    step = 1,
    minValue = 0,
    maxValue = steps ? steps.length - 1 : 100,
    selected = true,
    testId,
    value,
    ...rest
  } = props;
  const [isMoving, setIsMoving] = useState(false);
  const [selectedState, setSelectedState] = useState(typeof value === "undefined" ? selected : true);
  const [valueState, setValueState] = useSafeNumberValue(
    typeof value === "undefined" ? (maxValue - minValue) / 2 + minValue : value,
    minValue,
    maxValue
  );
  const errorTextUuid = useUuid(errorTextId);
  const titleId = useUuid();
  const labelLeftId = useUuid();
  const labelRightId = useUuid();
  const trackRef = useRef(null);
  const { refObject, isFocused } = usePseudoClasses(isMutableRefObject(ref) ? ref : null);
  const mergedRefs = mergeRefs([ref, refObject]);
  const { width: trackWidth } = useSize(trackRef) || { width: 0 };
  const largeStep = maxValue / 10;
  const invalid = !!errorText || !!error;
  useEffect(() => {
    const handlePointerUp = () => {
      setIsMoving(false);
    };
    document.addEventListener("pointerup", handlePointerUp);
    return () => {
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, []);
  const getValueBasedOnMarkerPosition = (markerPosition) => {
    var _a;
    const trackPosition = ((_a = trackRef.current) == null ? void 0 : _a.getBoundingClientRect().x) ?? 0;
    const normalizedPosition = (markerPosition - trackPosition) / trackWidth;
    const valueRange = maxValue - minValue;
    let value2 = normalizedPosition * valueRange + minValue;
    const stepCount = Math.round(value2 / step);
    value2 = stepCount * step;
    value2 = Math.max(minValue, Math.min(maxValue, value2));
    return value2;
  };
  useEffect(() => {
    const handlePointerMove = (e) => {
      if (!disabled && isMoving) {
        const newValue = getValueBasedOnMarkerPosition(e.clientX);
        setValueState(newValue);
      }
    };
    document.addEventListener("pointermove", handlePointerMove);
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
    };
  }, [isMoving]);
  useEffect(() => {
    if (value !== valueState && typeof value !== "undefined") {
      handleSelected();
      setValueState(value);
    }
  }, [value]);
  useEffect(() => {
    if (typeof value === "undefined" && selected !== selectedState) {
      setSelectedState(selected);
    }
  }, [selected]);
  const handleSelected = () => {
    if (selectedState === false) {
      setSelectedState(true);
    }
  };
  const handleKeyDown = (e) => {
    if (disabled) return;
    let flag = false;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        setValueState(valueState - step);
        flag = true;
        break;
      case "PageDown":
        setValueState(valueState - largeStep);
        flag = true;
        break;
      case "ArrowRight":
      case "ArrowUp":
        setValueState(valueState + step);
        flag = true;
        break;
      case "PageUp":
        setValueState(valueState + largeStep);
        flag = true;
        break;
      case "Home":
        setValueState(minValue);
        flag = true;
        break;
      case "End":
        setValueState(maxValue);
        flag = true;
        break;
    }
    if (flag) {
      handleSelected();
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const handleTrackClick = (e) => {
    var _a;
    if (disabled) return;
    const newValue = getValueBasedOnMarkerPosition(e.clientX);
    setValueState(newValue);
    (_a = refObject.current) == null ? void 0 : _a.focus();
  };
  const handlePointerDown = (e) => {
    var _a;
    if (disabled) return;
    setIsMoving(true);
    handleSelected();
    e.preventDefault();
    e.stopPropagation();
    (_a = refObject.current) == null ? void 0 : _a.focus();
  };
  const markerXPos = maxValue !== minValue ? trackWidth / (maxValue - minValue) * (valueState - minValue) : 0;
  const getAriaValueText = () => {
    const stepIndex = steps ? Math.round((valueState - minValue) / step) : null;
    if (steps && stepIndex !== null && stepIndex >= 0 && stepIndex < steps.length) {
      const step2 = steps[stepIndex];
      const emojiCode = step2.emojiUniCode;
      const label = typeof step2.label !== "undefined" ? step2.label.toString() : void 0;
      return emojiCode && label ? `${emojiCode} ${label}` : emojiCode || label;
    }
    return void 0;
  };
  const getAriaLabeledById = () => {
    if (title && labelLeft && labelRight) {
      return [titleId, labelLeftId, labelRightId].join(" ");
    }
    if (title && labelLeft) {
      return [titleId, labelLeftId].join(" ");
    }
    if (title && labelRight) {
      return [titleId, labelRightId].join(" ");
    }
    if (title) {
      return titleId;
    }
  };
  const ariaLabelAttributes = getAriaLabelAttributes({
    label: ariaLabel,
    id: getAriaLabeledById(),
    prefer: "label"
  });
  const getXPositionStyling = (index, stepsLength) => {
    return { left: `${index / (stepsLength - 1) * 100}%` };
  };
  useEffect(() => {
    if (selectedState && onChange) {
      onChange({
        target: {
          name: props.name,
          value: valueState
        }
      });
    }
  }, [valueState, selectedState]);
  const renderEmojies = () => {
    return /* @__PURE__ */ jsx("div", { className: styles["slider__emoji-container"], children: steps == null ? void 0 : steps.map((step2, index) => {
      return step2.emojiUniCode && /* @__PURE__ */ jsx(
        "div",
        {
          "aria-hidden": true,
          className: styles["slider__emoji"],
          style: getXPositionStyling(index, steps.length),
          children: step2.emojiUniCode
        },
        "emoji" + index
      );
    }) });
  };
  const renderSteps = () => {
    return steps == null ? void 0 : steps.map((_step, index) => {
      return /* @__PURE__ */ jsx("div", { className: styles["slider__track__step"], style: getXPositionStyling(index, steps.length) }, "step" + index);
    });
  };
  const renderStepLabels = () => {
    return /* @__PURE__ */ jsx("div", { className: styles["slider__value-container"], children: steps == null ? void 0 : steps.map((step2, index) => {
      return typeof step2.label !== "undefined" && /* @__PURE__ */ jsx(
        "div",
        {
          "aria-hidden": true,
          className: styles["slider__value"],
          style: getXPositionStyling(index, steps.length),
          children: step2.label
        },
        "label" + index
      );
    }) });
  };
  return /* @__PURE__ */ jsxs(ErrorWrapper, { className: errorWrapperClassName, errorText, errorTextId: errorTextUuid, children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        "aria-valuetext": getAriaValueText(),
        className: styles["sr-only-slider"],
        disabled,
        min: minValue,
        max: maxValue,
        onChange,
        onKeyDown: handleKeyDown,
        value: valueState,
        ref: mergedRefs,
        type: "range",
        ...rest,
        ...ariaLabelAttributes
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: styles.slider, "data-testid": testId, "data-analyticsid": AnalyticsId.Slider, children: [
      title && /* @__PURE__ */ jsx(Title, { className: styles["slider__title"], htmlMarkup: "h3", margin: 0, appearance: "title3", id: titleId, children: title }),
      /* @__PURE__ */ jsxs("div", { className: styles["slider__content-container"], children: [
        renderEmojies(),
        /* @__PURE__ */ jsxs(
          "div",
          {
            ref: trackRef,
            className: classNames(styles["slider__track-wrapper"], disabled && styles["slider__track-wrapper--disabled"]),
            onClick: handleTrackClick,
            onPointerDown: handlePointerDown,
            children: [
              /* @__PURE__ */ jsx("div", { className: classNames(styles.slider__track, disabled && styles["slider__track--disabled"]), children: renderSteps() }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: classNames(styles.slider__marker, {
                    [styles["slider__marker--disabled"]]: disabled,
                    [styles["slider__marker--selected"]]: selectedState,
                    [styles["slider__marker--invalid"]]: invalid,
                    [styles["slider__marker--focused"]]: !disabled && isFocused
                  }),
                  style: {
                    left: `${markerXPos}px`
                  }
                }
              )
            ]
          }
        ),
        renderStepLabels()
      ] }),
      (labelLeft || labelRight) && /* @__PURE__ */ jsxs("span", { className: styles.slider__options, children: [
        /* @__PURE__ */ jsx("span", { id: labelLeftId, children: labelLeft }),
        /* @__PURE__ */ jsx("span", { id: labelRightId, children: labelRight })
      ] })
    ] })
  ] });
});
Slider.displayName = "Slider";
export {
  Slider as S
};
//# sourceMappingURL=Slider.js.map
